import { EditOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { Col, Row, Popconfirm, Tooltip } from 'antd';
import ContactFormModal from 'components/Contacts/ContactFormModal';
import DealershipChooser from 'components/DealershipChooser';
import PitstopSimpleTable from 'components/PitstopSimpleTable';
import { makeObservable, observable, observe } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { PitstopButton } from 'shared';
import { ShopStore } from 'stores';
import AppStore from 'stores/App';
import ContactStore from 'stores/ContactStore';
import { CurrentUserStore } from 'stores';
import styled from 'styled-components';

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: #ffffff;
  padding: 25px 0 10px;
`;

const ActionButtonsWrapper = styled.div`
  right: 15px;

  button {
    margin-left: 10px;
  }
`;

const renderWithTooltip = (text) => {
  if (!text) return '-';

  const items = text
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  if (items.length <= 2) {
    return items.join(', ');
  }

  const visibleItems = items.slice(0, 2).join(', ');
  const hiddenItems = items.slice(2).join(', ');

  return (
    <>
      {visibleItems}
      <br />
      <Tooltip title={hiddenItems}>
        <span style={{ color: '#1890ff', cursor: 'pointer' }}>+ Show more</span>
      </Tooltip>
    </>
  );
};

class ContactPage extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object,
  };

  shopId = ShopStore.currentShop.id;
  CONTACT_FORM_MODAL_ID = 'contact_form_modal_id';

  state = {
    savingContact: false,
    loading: false,
    data: [],
    contactModalTitle: 'Add Vendor',
    selectedContactData: {},
  };

  columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Link onClick={(event) => this.onClickDetails(event, record)} to="/">
          {text}
        </Link>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: renderWithTooltip,
    },
    {
      title: 'Contact',
      dataIndex: 'sales_person',
      key: 'sales_person',
      render: renderWithTooltip,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: renderWithTooltip,
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      align: 'right',
      render: (a, record) => {
        return (
          <Fragment>
            <EditOutlined
              onClick={() => this.onClickEdit(record)}
              style={{ marginRight: 8 }}
            />
            {ShopStore.currentShop.id === record.id_shop && (
              <>
                {CurrentUserStore.user.allowManager && (
                  <Popconfirm
                    placement="topRight"
                    title={'Are you sure you want to delete this vendor?'}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                    onConfirm={(event) => {
                      event.stopPropagation();
                      this.onConfirmDeletePart(record);
                    }}
                    onCancel={(event) => {
                      event.stopPropagation();
                    }}
                    okText="Yes"
                    cancelText="No"
                  >
                    <DeleteOutlined />
                  </Popconfirm>
                )}
              </>
            )}
          </Fragment>
        );
      },
    },
  ];

  disposer = observe(ShopStore.currentShop, 'id', async () => {
    return this.refreshShopContacts(ShopStore.currentShop.id);
  });

  componentWillUnmount() {
    this.disposer();
  }

  componentDidMount() {
    AppStore.setSelectedSidebarKey('/contacts');
    this.refreshShopContacts();
  }

  onClickAddContact = () => {
    this.setState(
      {
        contactModalTitle: 'Add Vendor',
        selectedContactData: {},
      },
      () => {
        AppStore.openModals.set(this.CONTACT_FORM_MODAL_ID, true);
      }
    );
  };

  onClickDetails = (event, contact) => {
    event.preventDefault();
    this.onClickEdit(contact);
  };

  /**
   * Open modal to edit contact
   */
  onClickEdit = async (contact) => {
    this.setState(
      {
        contactModalTitle: 'Edit Contact',
        selectedContactData: { ...contact, salesPerson: contact.sales_person },
      },
      () => {
        AppStore.openModals.set(this.CONTACT_FORM_MODAL_ID, true);
      }
    );
  };

  /**
   * Delete contact
   * @param {Object} contact
   */
  onConfirmDelete = async (contact) => {
    try {
      await this.setState({ loading: true });
      await ContactStore.deleteShopContact(contact.id);
      AppStore.addSuccess('Contact deleted successfully');
      await this.refreshShopContacts();
    } catch (error) {
      AppStore.addError('Error to delete contact: ' + error);
    } finally {
      this.setState({ loading: false });
    }
  };

  refreshShopContacts = async (shopId = ShopStore.currentShop.id) => {
    try {
      await this.setState({ loading: true });
      const contacts = await ContactStore.getShopContacts(shopId);
      await this.setState({ data: contacts });
    } catch (error) {
      console.log(error);
    } finally {
      this.setState({ loading: false });
    }
  };

  onSubmitContactForm = async (data) => {
    try {
      await this.setState({ savingContact: true });
      if (data.contactId) {
        // update the contact
        await ContactStore.updateShopContact(data.contactId, {
          ...data,
        });
      } else {
        // create a new contact
        await ContactStore.createShopContact({
          ...data,
          shopId: ShopStore.currentShop.id,
        });
      }
      AppStore.addSuccess('Contact saved successfully');
      await this.refreshShopContacts();
      // clear the form
      this.setState({ selectedContactData: {} });
      AppStore.openModals.set(this.CONTACT_FORM_MODAL_ID, false);
    } catch (error) {
      console.log(error);
      AppStore.addError('Error to save contact: ' + error.message);
    } finally {
      await this.setState({ savingContact: false });
    }
  };

  onConfirmDeletePart = async (record) => {
    try {
      const partId = record.id;

      const response = await ContactStore.deleteShopContact(partId);

      if (response?.data === 'Contact successfully deleted') {
        AppStore.addSuccess('The contact was deleted!');
        this.refreshShopContacts();
      }
    } catch (error) {
      console.error(error);
      AppStore.addError('There was an error deleting the vendor');
    }
  };

  render() {
    return (
      <Fragment>
        <ContactFormModal
          modalId={this.CONTACT_FORM_MODAL_ID}
          saving={this.state.savingContact}
          title={this.state.contactModalTitle}
          defaultData={this.state.selectedContactData}
          onCancel={() => {
            this.setState({ selectedContactData: {} });
            AppStore.openModals.set(this.CONTACT_FORM_MODAL_ID, false);
          }}
          onSubmit={(data) => {
            this.onSubmitContactForm(data);
          }}
        />

        <DealershipChooser />
        <ActionsWrapper>
          <h1>Vendor Management</h1>
          <ActionButtonsWrapper>
            <PitstopButton
              icon={<PlusOutlined />}
              onClick={() => this.onClickAddContact()}
            >
              Add Vendor
            </PitstopButton>
          </ActionButtonsWrapper>
        </ActionsWrapper>

        <Row>
          <Col span={24}>
            <PitstopSimpleTable
              loading={this.state.loading}
              columns={this.columns}
              data={this.state.data}
            />
          </Col>
        </Row>
      </Fragment>
    );
  }
  constructor() {
    super();
    makeObservable(this, {
      shopId: observable,
    });
  }
}

export default withRouter(observer(ContactPage));
